import { useCallback } from "react";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import DataVisualizationTypeContext from "components/charting/contexts/DataVisualizationTypeContext";
import SetDataVisualizationTypeContext from "components/charting/contexts/SetDataVisualizationTypeContext";
import { SelectedChartMeasure } from "components/charting/types";
import { emptyMeasure } from "features/Charting/Charting.config";
import ChartHelpers from "features/Charting/helpers/ChartHelpers";
import useSetChartSelections from "features/Charting/hooks/useSetChartSelections";
import useDataVisualisationQueryState from "components/charting/hooks/useDataVisualisationQueryState";
import { athleteProfilingV2Enabled } from "config/features";

function DataVisualizationTypeProvider(props: { children: React.ReactNode }) {
  const [dataVisualizationType, setDataVisualizationType] = useDataVisualisationQueryState();
  const setSelections = useSetChartSelections();

  const onSetDataVisualizationType = useCallback(
    (type: DataVisualizationType) => {
      if (ChartHelpers.restrictsMeasures(type)) {
        setSelections((prev) => {
          const filteredExercises = prev.measures.filter((measure) =>
            ChartHelpers.isMeasureAllowedForDataVisType(measure, { visType: type })
          );
          const measure = filteredExercises.concat(emptyMeasure).shift() as SelectedChartMeasure;

          // V2 derives the profiling rep metric from the profile type instead.
          if (!athleteProfilingV2Enabled && type === DataVisualizationType.PROFILING_CHART && measure.repMetric) {
            const repetitionsMetadata = ChartHelpers.filterProfilingRepMetrics(measure.metadata.repetitions, {
              visType: type,
            });

            return {
              measures: [
                {
                  ...measure,
                  repMetric:
                    repetitionsMetadata.find((rep) => rep.field === measure.repMetric?.field) ||
                    repetitionsMetadata[0] ||
                    null,
                },
              ],
            };
          }

          return { measures: [measure] };
        });
      }

      setDataVisualizationType(type);
    },
    [setDataVisualizationType, setSelections]
  );

  return (
    <SetDataVisualizationTypeContext.Provider value={onSetDataVisualizationType}>
      <DataVisualizationTypeContext.Provider value={dataVisualizationType}>
        {props.children}
      </DataVisualizationTypeContext.Provider>
    </SetDataVisualizationTypeContext.Provider>
  );
}

export default DataVisualizationTypeProvider;
